/**
 * EmailTemplatesPage.jsx
 * 
 * Admin interface for managing Email Templates.
 * Email templates define reusable formats for scheduled reports and notifications
 * with support for placeholders that are replaced at send time.
 * 
 * Features:
 * - CRUD operations for email templates
 * - Rich HTML editor for email body
 * - Live preview with sample placeholders
 * - Color picker for branding
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/EmailTemplatesStyles.scss';
import PageHeader from '@/components/Common/PageHeader';
import { FaChevronDown, FaEdit, FaEnvelope, FaEye, FaFolder, FaInfoCircle, FaPlus, FaSave, FaSearch, FaSpinner, FaTimes, FaTrash } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Available placeholders for email templates.
 */
const PLACEHOLDERS = [
    // General
    { key: '{Date}', description: 'Current date (full format)', example: '28 November 2025', group: 'General' },
    { key: '{ShortDate}', description: 'Current date (short format)', example: '28/11/2025', group: 'General' },
    { key: '{Time}', description: 'Current time (24-hour)', example: '14:30', group: 'General' },
    { key: '{DateTime}', description: 'Date and time combined', example: '28 November 2025 14:30', group: 'General' },
    { key: '{Month}', description: 'Current month name', example: 'November', group: 'General' },
    { key: '{Year}', description: 'Current year', example: '2025', group: 'General' },
    { key: '{RecipientName}', description: 'Recipient\'s display name', example: 'John Smith', group: 'General' },
    { key: '{CompanyName}', description: 'Company name from settings', example: 'Capital Land Management Services', group: 'General' },
    // Reports
    { key: '{ReportName}', description: 'Name of the report', example: 'Weekly Sales Report', group: 'Reports' },
    { key: '{RowCount}', description: 'Number of rows in report', example: '42', group: 'Reports' },
    { key: '{AttachmentName}', description: 'Name of attached file', example: 'Report_2025-11-28.xlsx', group: 'Reports' },
    // Action Items & Tickets
    { key: '{TicketId}', description: 'Action item or ticket ID', example: '1042', group: 'Action Items' },
    { key: '{TicketTitle}', description: 'Action item or ticket title', example: 'Server room AC fault', group: 'Action Items' },
    { key: '{SenderName}', description: 'Name of the original sender', example: 'Jane Doe', group: 'Action Items' },
    { key: '{FunctionalArea}', description: 'Functional area of the mailbox', example: 'IT Service Management', group: 'Action Items' },
    { key: '{Priority}', description: 'Action item priority', example: 'High', group: 'Action Items' },
    { key: '{Status}', description: 'Action item status', example: 'Open', group: 'Action Items' },
    { key: '{AssignedTo}', description: 'Assigned user name', example: 'John Smith', group: 'Action Items' },
    { key: '{DueDate}', description: 'Action item due date', example: '15/07/2025', group: 'Action Items' }
];

/**
 * Default categories for email templates.
 */
const DEFAULT_CATEGORIES = ['Reports', 'Notifications', 'Alerts', 'Summaries', 'Action Items', 'IT Service Management', 'Auto-Response'];

/**
 * EmailTemplatesPage Component
 * Main component for managing email templates.
 */
const EmailTemplatesPage = () => {
    const { apiFetch } = useAuth();
    
    // Templates state
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewHtml, setPreviewHtml] = useState(null);
    
    // Categories
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    
    // Filter state
    const [showInactive, setShowInactive] = useState(false);
    const [filterCategory, setFilterCategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredTemplates = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter(t => {
            const hay = [t.displayName, t.templateKey, t.description, t.category, t.subjectTemplate]
                .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [templates, searchQuery]);

    // Get current user ID
    const currentUserId = parseInt(localStorage.getItem('userID') || '0', 10);

    /**
     * Fetch email templates from API.
     */
    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            let url = `/api/EmailTemplates?includeInactive=${showInactive}`;
            if (filterCategory) {
                url += `&category=${encodeURIComponent(filterCategory)}`;
            }
            
            const response = await apiFetch(url);
            const data = await response.json();
            
            if (data.success) {
                setTemplates(data.data || []);
            } else {
                toast.error(data.message || 'Failed to load email templates');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching email templates:', error);
            toast.error('Error loading email templates');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, showInactive, filterCategory]);

    /**
     * Fetch categories from API.
     */
    const fetchCategories = useCallback(async () => {
        try {
            const response = await apiFetch('/api/EmailTemplates/categories');
            const data = await response.json();
            if (data.success) {
                setCategories(data.data || DEFAULT_CATEGORIES);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching categories:', error);
        }
    }, [apiFetch]);

    // Load data on mount
    useEffect(() => {
        fetchTemplates();
        fetchCategories();
    }, [fetchTemplates, fetchCategories]);

    /**
     * Handle creating new template.
     */
    const handleCreateTemplate = () => {
        setEditingTemplate(null);
        setShowModal(true);
    };

    /**
     * Handle editing existing template.
     */
    const handleEditTemplate = async (template) => {
        try {
            const response = await apiFetch(`/api/EmailTemplates/${template.emailTemplateId}`);
            const data = await response.json();
            
            if (data.success) {
                setEditingTemplate(data.data);
                setShowModal(true);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching template details:', error);
            toast.error('Error loading template details');
        }
    };

    /**
     * Handle previewing template.
     */
    const handlePreviewTemplate = async (template) => {
        try {
            const response = await apiFetch(`/api/EmailTemplates/${template.emailTemplateId}/preview`);
            const data = await response.json();
            
            if (data.success) {
                setPreviewHtml(data.data.bodyHtml);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error previewing template:', error);
            toast.error('Error loading template preview');
        }
    };

    /**
     * Handle deleting template.
     */
    const handleDeleteTemplate = async (template) => {
        if (!confirm(`Are you sure you want to delete "${template.displayName}"?`)) return;
        
        try {
            const response = await apiFetch(
                `/api/EmailTemplates/${template.emailTemplateId}`,
                { method: 'DELETE' }
            );
            const data = await response.json();
            
            if (data.success) {
                toast.success(data.message);
                fetchTemplates();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting template:', error);
            toast.error('Error deleting template');
        }
    };

    /**
     * Handle saving template (create or update).
     */
    const handleSaveTemplate = async (templateData) => {
        try {
            const payload = {
                ...templateData,
                createdByUserId: currentUserId,
                modifiedByUserId: currentUserId
            };

            let response;
            if (editingTemplate) {
                response = await apiFetch(
                    `/api/EmailTemplates/${editingTemplate.emailTemplateId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    }
                );
            } else {
                response = await apiFetch(
                    '/api/EmailTemplates',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    }
                );
            }

            const data = await response.json();
            
            if (data.success) {
                toast.success(data.message);
                setShowModal(false);
                fetchTemplates();
                fetchCategories();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving template:', error);
            toast.error('Error saving email template');
        }
    };

    /**
     * Close modal.
     */
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingTemplate(null);
    };

    /**
     * Close preview.
     */
    const handleClosePreview = () => {
        setPreviewHtml(null);
    };

    /**
     * Render the templates list.
     */
    const renderTemplatesList = () => {
        if (loading) {
            return (
                <div className="loading-state">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading email templates...</p>
                </div>
            );
        }

        if (templates.length === 0) {
            return (
                <div className="empty-state">
                    <FaEnvelope className="fa-3x" />
                    <h3>No Email Templates</h3>
                    <p>Create your first email template for scheduled reports and notifications.</p>
                    <button className="btn btn-primary" onClick={handleCreateTemplate}>
                        <FaPlus /> Create Email Template
                    </button>
                </div>
            );
        }

        if (filteredTemplates.length === 0) {
            return (
                <div className="empty-state">
                    <FaSearch className="fa-3x" />
                    <h3>No matches</h3>
                    <p>No templates match <strong>&ldquo;{searchQuery}&rdquo;</strong>.</p>
                    <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                        Clear search
                    </button>
                </div>
            );
        }

        return (
            <div className="templates-grid">
                {filteredTemplates.map(template => (
                    <div 
                        key={template.emailTemplateId} 
                        className={`template-card ${!template.isActive ? 'inactive' : ''}`}
                        style={{ borderColor: template.primaryColor || '#dc3545' }}
                    >
                        <div className="card-header">
                            <div className="card-title">
                                <div 
                                    className="color-indicator"
                                    style={{ backgroundColor: template.primaryColor || '#dc3545' }}
                                ></div>
                                <h3>{template.displayName}</h3>
                                {template.isDefault && <span className="badge badge-default">Default</span>}
                                {!template.isActive && <span className="badge badge-inactive">Inactive</span>}
                            </div>
                            <div className="card-actions">
                                <button 
                                    className="btn btn-icon" 
                                    onClick={() => handlePreviewTemplate(template)}
                                    title="Preview"
                                >
                                    <FaEye />
                                </button>
                                <button 
                                    className="btn btn-icon" 
                                    onClick={() => handleEditTemplate(template)}
                                    title="Edit"
                                >
                                    <FaEdit />
                                </button>
                                <button 
                                    className="btn btn-icon btn-danger"
                                    onClick={() => handleDeleteTemplate(template)}
                                    title="Delete"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                        
                        <div className="card-body">
                            {template.description && <p className="description">{template.description}</p>}

                            <div className="card-meta">
                                {template.category && (
                                    <div className="meta-item">
                                        <FaFolder />
                                        <span>{template.category}</span>
                                    </div>
                                )}
                            </div>

                            <div className="subject-preview">
                                <strong>Subject:</strong> {template.subjectTemplate}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="email-templates-page">
            <PageHeader
                icon={<FaEnvelope />}
                title="Email Template Designer"
                description={<>Configure reusable email templates for scheduled reports, action-item notifications, IT tickets, and auto-responses. Templates can be linked to mailboxes on the <strong>Email Mailboxes</strong> page.</>}
                meta={(
                    <>
                        <span className="ctx-page-header-pill tone-info">
                            {templates.length} template{templates.length === 1 ? '' : 's'}
                        </span>
                        {showInactive && (
                            <span className="ctx-page-header-pill tone-warn">Including inactive</span>
                        )}
                    </>
                )}
                primaryAction={(
                    <button className="btn btn-primary" onClick={handleCreateTemplate}>
                        <FaPlus /> New Template
                    </button>
                )}
                toolbar={(
                    <>
                        <div className="tpl-search" role="search">
                            <FaSearch className="tpl-search-icon" aria-hidden="true" />
                            <input
                                type="search"
                                className="form-control tpl-search-input"
                                placeholder="Search by name, key, subject, or description..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search email templates"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    className="tpl-search-clear"
                                    aria-label="Clear search"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        <select
                            className="filter-select"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            aria-label="Filter by category"
                        >
                            <option value="">All categories</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>

                        <label className="tpl-inline-check">
                            <input
                                type="checkbox"
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                            Show inactive
                        </label>
                    </>
                )}
            />

            {/* Placeholders Reference */}
            <div className="placeholders-info">
                <button 
                    className="collapse-toggle"
                    onClick={() => document.querySelector('.placeholders-content').classList.toggle('expanded')}
                >
                    <FaInfoCircle />
                    Available Placeholders
                    <FaChevronDown />
                </button>
                <div className="placeholders-content">
                    {['General', 'Reports', 'Action Items'].map(group => {
                        const groupPlaceholders = PLACEHOLDERS.filter(p => p.group === group);
                        if (groupPlaceholders.length === 0) return null;
                        return (
                            <div key={group} style={{ marginBottom: '12px' }}>
                                <h4 style={{ fontSize: '13px', color: '#666', marginBottom: '6px', fontWeight: 600 }}>{group}</h4>
                                <div className="placeholders-grid">
                                    {groupPlaceholders.map(p => (
                                        <div key={p.key} className="placeholder-item">
                                            <code>{p.key}</code>
                                            <span className="ph-desc">{p.description}</span>
                                            <span className="ph-example">e.g., {p.example}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Content */}
            <div className="templates-content">
                {renderTemplatesList()}
            </div>

            {/* Template Modal */}
            {showModal && (
                <TemplateModal
                    template={editingTemplate}
                    categories={categories}
                    onSave={handleSaveTemplate}
                    onClose={handleCloseModal}
                />
            )}

            {/* Preview Modal */}
            {previewHtml && (
                <div className="modal-backdrop" onClick={handleClosePreview}>
                    <div className="modal-content preview-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <FaEye />
                                Email Preview
                            </h2>
                            <button className="close-btn" onClick={handleClosePreview}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body preview-body">
                            <iframe 
                                srcDoc={previewHtml}
                                title="Email Preview"
                                sandbox="allow-same-origin"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Generate a URL-safe slug from a display name.
 */
function generateSlug(name) {
    return (name || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/**
 * Get a default subject template based on category.
 */
function getDefaultSubjectForCategory(category) {
    switch (category) {
        case 'Action Items':
            return 'Action Item #{TicketId}: {TicketTitle}';
        case 'IT Service Management':
            return 'IT Ticket #{TicketId}: {TicketTitle}';
        case 'Auto-Response':
            return 'Re: {TicketTitle} [#{TicketId}]';
        case 'Alerts':
            return '⚠️ Alert: {ReportName} - {ShortDate}';
        case 'Notifications':
            return 'Notification: {ReportName} - {ShortDate}';
        default:
            return 'Scheduled Report: {ReportName} - {ShortDate}';
    }
}

/**
 * Get relevant placeholder keys based on the selected template category.
 */
function getRelevantPlaceholders(category) {
    const general = PLACEHOLDERS.filter(p => p.group === 'General').map(p => p.key);
    const reports = PLACEHOLDERS.filter(p => p.group === 'Reports').map(p => p.key);
    const actionItems = PLACEHOLDERS.filter(p => p.group === 'Action Items').map(p => p.key);

    switch (category) {
        case 'Action Items':
        case 'IT Service Management':
        case 'Auto-Response':
            return [...actionItems, ...general];
        case 'Reports':
        case 'Summaries':
            return [...reports, ...general];
        default:
            return [...general, ...reports, ...actionItems];
    }
}

/**
 * TemplateModal Component
 * Modal for creating/editing email templates.
 */
const TemplateModal = ({ template, categories, onSave, onClose }) => {
    const isEditMode = !!template;

    const [formData, setFormData] = useState({
        templateKey: template?.templateKey || '',
        displayName: template?.displayName || '',
        description: template?.description || '',
        category: template?.category || 'Reports',
        subjectTemplate: template?.subjectTemplate || 'Scheduled Report: {ReportName} - {ShortDate}',
        bodyHtmlTemplate: template?.bodyHtmlTemplate || getDefaultBodyTemplate(),
        headerHtml: template?.headerHtml || '',
        footerHtml: template?.footerHtml || '',
        primaryColor: template?.primaryColor || '#dc3545',
        secondaryColor: template?.secondaryColor || '#6c757d',
        logoUrl: template?.logoUrl || '',
        isActive: template?.isActive ?? true,
        isDefault: template?.isDefault ?? false
    });

    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');

    /**
     * Handle form field change.
     * Auto-generates templateKey from displayName in create mode.
     * Updates subject template when category changes (if still default).
     */
    const handleChange = (field, value) => {
        setFormData(prev => {
            const next = { ...prev, [field]: value };

            // Auto-generate templateKey from displayName in create mode
            if (field === 'displayName' && !isEditMode) {
                next.templateKey = generateSlug(value);
            }

            // When category changes, update the subject template if the user hasn't customized it
            if (field === 'category' && !isEditMode) {
                const isDefaultSubject = Object.values({
                    'Action Items': getDefaultSubjectForCategory('Action Items'),
                    'IT Service Management': getDefaultSubjectForCategory('IT Service Management'),
                    'Auto-Response': getDefaultSubjectForCategory('Auto-Response'),
                    'Alerts': getDefaultSubjectForCategory('Alerts'),
                    'Notifications': getDefaultSubjectForCategory('Notifications'),
                    'default': getDefaultSubjectForCategory('Reports'),
                }).includes(prev.subjectTemplate) || !prev.subjectTemplate;

                if (isDefaultSubject) {
                    next.subjectTemplate = getDefaultSubjectForCategory(value);
                }
            }

            return next;
        });
    };

    /**
     * Insert placeholder at cursor position.
     */
    const insertPlaceholder = (placeholder, fieldId) => {
        const field = document.getElementById(fieldId);
        if (field) {
            const start = field.selectionStart;
            const end = field.selectionEnd;
            const text = field.value;
            const newText = text.substring(0, start) + placeholder + text.substring(end);
            handleChange(fieldId.replace('input-', ''), newText);
            
            // Set cursor after placeholder
            setTimeout(() => {
                field.focus();
                field.setSelectionRange(start + placeholder.length, start + placeholder.length);
            }, 0);
        }
    };

    /**
     * Replace placeholders in a string with sample/example values for preview.
     * @param {string} text - The text containing placeholders
     * @returns {string} - Text with placeholders replaced by sample values
     */
    const replacePlaceholdersForPreview = (text) => {
        if (!text) return '';
        
        let result = text;
        PLACEHOLDERS.forEach(p => {
            // Replace all occurrences of the placeholder with its example value
            result = result.split(p.key).join(p.example);
        });
        return result;
    };

    /**
     * Generate the full HTML email preview with styles applied.
     * @returns {string} - Complete HTML document for preview
     */
    const generatePreviewHtml = () => {
        const subject = replacePlaceholdersForPreview(formData.subjectTemplate);
        const header = replacePlaceholdersForPreview(formData.headerHtml);
        const body = replacePlaceholdersForPreview(formData.bodyHtmlTemplate);
        const footer = replacePlaceholdersForPreview(formData.footerHtml);
        
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        color: #333;
                        max-width: 600px;
                        margin: 0 auto;
                        padding: 20px;
                        background-color: #f5f5f5;
                    }
                    .email-container {
                        background: white;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    }
                    .email-header {
                        background-color: ${formData.primaryColor};
                        color: white;
                        padding: 20px;
                        text-align: center;
                    }
                    .email-header img {
                        max-height: 50px;
                        margin-bottom: 10px;
                    }
                    .email-subject {
                        background: #f8f9fa;
                        padding: 15px 20px;
                        border-bottom: 1px solid #dee2e6;
                        font-weight: bold;
                        color: ${formData.secondaryColor};
                    }
                    .email-body {
                        padding: 20px;
                    }
                    .email-body h2, .email-body h3 {
                        color: ${formData.primaryColor};
                    }
                    .email-body ul {
                        background: #f8f9fa;
                        padding: 15px 15px 15px 35px;
                        border-radius: 4px;
                    }
                    .email-footer {
                        background: #f8f9fa;
                        padding: 15px 20px;
                        text-align: center;
                        color: ${formData.secondaryColor};
                        font-size: 12px;
                        border-top: 1px solid #dee2e6;
                    }
                </style>
            </head>
            <body>
                <div class="email-container">
                    ${header ? `<div class="email-header">${formData.logoUrl ? `<img src="${formData.logoUrl}" alt="Logo" />` : ''}${header}</div>` : 
                      `<div class="email-header">${formData.logoUrl ? `<img src="${formData.logoUrl}" alt="Logo" />` : ''}<h1>Email Preview</h1></div>`}
                    <div class="email-subject">
                        <strong>Subject:</strong> ${subject}
                    </div>
                    <div class="email-body">
                        ${body}
                    </div>
                    ${footer ? `<div class="email-footer">${footer}</div>` : 
                      '<div class="email-footer">This is an automated email from CapTrac.</div>'}
                </div>
            </body>
            </html>
        `;
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.displayName.trim()) {
            toast.error('Display Name is required');
            return;
        }

        // Auto-generate templateKey if somehow still empty
        const finalData = { ...formData };
        if (!finalData.templateKey.trim()) {
            finalData.templateKey = generateSlug(finalData.displayName);
        }
        if (!finalData.templateKey.trim()) {
            toast.error('Could not generate a template key. Please provide a display name.');
            return;
        }

        if (!finalData.subjectTemplate.trim()) {
            toast.error('Subject Template is required');
            return;
        }

        if (!finalData.bodyHtmlTemplate.trim()) {
            toast.error('Body HTML Template is required');
            return;
        }

        setSaving(true);
        try {
            await onSave(finalData);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <FaEnvelope />
                        {template ? 'Edit Email Template' : 'Create Email Template'}
                    </h2>
                    <button className="close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Tab Navigation */}
                    <div className="modal-tabs">
                        <button 
                            type="button"
                            className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                            onClick={() => setActiveTab('basic')}
                        >
                            Basic Info
                        </button>
                        <button 
                            type="button"
                            className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
                            onClick={() => setActiveTab('content')}
                        >
                            Content
                        </button>
                        <button 
                            type="button"
                            className={`tab-btn ${activeTab === 'styling' ? 'active' : ''}`}
                            onClick={() => setActiveTab('styling')}
                        >
                            Styling
                        </button>
                        <button 
                            type="button"
                            className={`tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
                            onClick={() => setActiveTab('preview')}
                        >
                            <FaEye /> Preview
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Basic Info Tab */}
                        {activeTab === 'basic' && (
                            <div className="tab-content">
                                <div className="form-group">
                                    <label>Template Name *</label>
                                    <input
                                        type="text"
                                        value={formData.displayName}
                                        onChange={(e) => handleChange('displayName', e.target.value)}
                                        placeholder="e.g., Action Item Notification, IT Ticket Auto-Response"
                                        required
                                        autoFocus
                                    />
                                    {formData.templateKey && (
                                        <small style={{ color: '#999' }}>
                                            Key: <code style={{ fontSize: '11px', background: '#f5f5f5', padding: '1px 5px', borderRadius: '3px' }}>{formData.templateKey}</code>
                                        </small>
                                    )}
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Category *</label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => handleChange('category', e.target.value)}
                                        >
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                        <small>Choose the purpose of this template</small>
                                    </div>
                                    <div className="form-group checkbox-group-inline">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                            />
                                            Active
                                        </label>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.isDefault}
                                                onChange={(e) => handleChange('isDefault', e.target.checked)}
                                            />
                                            Default for Category
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        placeholder="Describe when this template should be used..."
                                        rows={2}
                                    />
                                </div>

                                {/* Quick-start hint based on selected category */}
                                {!isEditMode && (
                                    <div className="category-hint" style={{
                                        background: '#f0f8ff', borderLeft: '3px solid #4a90d9',
                                        padding: '12px 16px', borderRadius: '4px', marginTop: '8px', fontSize: '13px', color: '#555'
                                    }}>
                                        <strong>💡 Tip:</strong>{' '}
                                        {formData.category === 'Action Items' && 'Use placeholders like {TicketId}, {TicketTitle}, {Priority}, and {AssignedTo} to create dynamic action item notifications.'}
                                        {formData.category === 'IT Service Management' && 'Use placeholders like {TicketId}, {TicketTitle}, and {FunctionalArea} for IT ticket notifications. Link this template to a mailbox on the Email Mailboxes page.'}
                                        {formData.category === 'Auto-Response' && 'Auto-response templates are sent automatically when a new item is created from an inbound email. Configure them under Email Mailboxes → Auto-Response.'}
                                        {formData.category === 'Reports' && 'Report templates are used for scheduled report emails. Use {ReportName}, {RowCount}, and {AttachmentName} placeholders.'}
                                        {formData.category === 'Notifications' && 'Notification templates are used for general system notifications and updates.'}
                                        {formData.category === 'Alerts' && 'Alert templates are used for urgent system alerts and warnings.'}
                                        {formData.category === 'Summaries' && 'Summary templates are used for periodic digest emails.'}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Content Tab */}
                        {activeTab === 'content' && (
                            <div className="tab-content">
                                <div className="form-group">
                                    <label>Subject Template *</label>
                                    <div className="input-with-placeholders">
                                        <input
                                            id="input-subjectTemplate"
                                            type="text"
                                            value={formData.subjectTemplate}
                                            onChange={(e) => handleChange('subjectTemplate', e.target.value)}
                                            placeholder={getDefaultSubjectForCategory(formData.category)}
                                            required
                                        />
                                        <div className="placeholder-buttons">
                                            {getRelevantPlaceholders(formData.category).slice(0, 4).map(ph => (
                                                <button 
                                                    key={ph}
                                                    type="button" 
                                                    className="ph-btn"
                                                    onClick={() => insertPlaceholder(ph, 'input-subjectTemplate')}
                                                >
                                                    {ph}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Body HTML Template *</label>
                                    <div className="input-with-placeholders">
                                        <textarea
                                            id="input-bodyHtmlTemplate"
                                            value={formData.bodyHtmlTemplate}
                                            onChange={(e) => handleChange('bodyHtmlTemplate', e.target.value)}
                                            rows={12}
                                            className="code-input"
                                            required
                                        />
                                        <div className="placeholder-buttons">
                                            {getRelevantPlaceholders(formData.category).map(ph => (
                                                <button 
                                                    key={ph}
                                                    type="button" 
                                                    className="ph-btn"
                                                    onClick={() => insertPlaceholder(ph, 'input-bodyHtmlTemplate')}
                                                    title={PLACEHOLDERS.find(p => p.key === ph)?.description || ''}
                                                >
                                                    {ph}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <small>HTML content for the email body. Use the placeholder buttons above to insert dynamic values.</small>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Header HTML (optional)</label>
                                        <textarea
                                            value={formData.headerHtml}
                                            onChange={(e) => handleChange('headerHtml', e.target.value)}
                                            rows={3}
                                            placeholder="<h1>{CompanyName}</h1>"
                                            className="code-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Footer HTML (optional)</label>
                                        <textarea
                                            value={formData.footerHtml}
                                            onChange={(e) => handleChange('footerHtml', e.target.value)}
                                            rows={3}
                                            placeholder="<p>This is an automated email.</p>"
                                            className="code-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Styling Tab */}
                        {activeTab === 'styling' && (
                            <div className="tab-content">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Primary Color</label>
                                        <div className="color-input-group">
                                            <input
                                                type="color"
                                                value={formData.primaryColor}
                                                onChange={(e) => handleChange('primaryColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                value={formData.primaryColor}
                                                onChange={(e) => handleChange('primaryColor', e.target.value)}
                                                placeholder="#dc3545"
                                            />
                                        </div>
                                        <small>Used for headers, buttons, and accents</small>
                                    </div>
                                    <div className="form-group">
                                        <label>Secondary Color</label>
                                        <div className="color-input-group">
                                            <input
                                                type="color"
                                                value={formData.secondaryColor}
                                                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                value={formData.secondaryColor}
                                                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                                                placeholder="#6c757d"
                                            />
                                        </div>
                                        <small>Used for secondary text and borders</small>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Logo URL (optional)</label>
                                    <input
                                        type="url"
                                        value={formData.logoUrl}
                                        onChange={(e) => handleChange('logoUrl', e.target.value)}
                                        placeholder="https://example.com/logo.png"
                                    />
                                    <small>Full URL to company logo image</small>
                                </div>

                                {/* Color Preview */}
                                <div className="color-preview">
                                    <h4>Preview</h4>
                                    <div 
                                        className="preview-header"
                                        style={{ backgroundColor: formData.primaryColor }}
                                    >
                                        {formData.logoUrl && <img src={formData.logoUrl} alt="Logo" />}
                                        <span>Email Header</span>
                                    </div>
                                    <div className="preview-content">
                                        <p>This is how your email content will look with the selected colors.</p>
                                        <button 
                                            type="button"
                                            style={{ backgroundColor: formData.primaryColor }}
                                        >
                                            Sample Button
                                        </button>
                                    </div>
                                    <div 
                                        className="preview-footer"
                                        style={{ color: formData.secondaryColor }}
                                    >
                                        Footer text with secondary color
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Preview Tab - Live email preview with sample data */}
                        {activeTab === 'preview' && (
                            <div className="tab-content preview-tab">
                                <div className="preview-info">
                                    <FaInfoCircle />
                                    <span>This preview shows how the email will look with sample placeholder values.</span>
                                </div>
                                <div className="email-preview-container">
                                    <iframe 
                                        srcDoc={generatePreviewHtml()}
                                        title="Email Template Preview"
                                        sandbox="allow-same-origin"
                                        className="email-preview-iframe"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> {template ? 'Update' : 'Create'} Template
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/**
 * Returns the default body HTML template.
 */
function getDefaultBodyTemplate() {
    return `<h2>Scheduled Report: {ReportName}</h2>

<p>Dear {RecipientName},</p>

<p>Please find attached your scheduled report. This report was generated on <strong>{DateTime}</strong>.</p>

<h3>Report Summary</h3>
<ul>
    <li><strong>Report Name:</strong> {ReportName}</li>
    <li><strong>Generated:</strong> {DateTime}</li>
    <li><strong>Records:</strong> {RowCount} rows</li>
    <li><strong>Attachment:</strong> {AttachmentName}</li>
</ul>

<p>If you have any questions about this report, please contact your system administrator.</p>

<p>Best regards,<br>
{CompanyName}</p>`;
}

export default EmailTemplatesPage;
